import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import moment from 'moment-timezone';
import { DateRangePipe } from '../../../pipes/date-range.pipe';
import { ClockService } from '../../../services/clock.service';
import { DATE_INPUT_FORMAT } from '../../../utils/input-formats';

/**
 * Navigates the weeks from the current week, optionally with weekends. A
 * legend for what's on the calendar can be projected below.
 */
@Component({
  selector: 'app-schedule-calendar-header',
  templateUrl: './schedule-calendar-header.component.html',
  styleUrls: ['./schedule-calendar-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateRangePipe,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
  ],
})
export class ScheduleCalendarHeaderComponent {
  readonly timezoneName = input.required<string>();

  private readonly clock = inject(ClockService);

  private readonly _showWeekends = signal(false);
  readonly showWeekends = this._showWeekends.asReadonly();

  /** Weeks from the current week, which is the earliest navigable week */
  private readonly weekOffset = signal(0);

  /**
   * Start of the selected week's Monday in the timezone, following the clock
   * so the current week moves on at midnight on Monday
   */
  private readonly weekStart = computed(() =>
    moment(this.clock.now())
      .tz(this.timezoneName())
      .startOf('isoWeek')
      .add(this.weekOffset(), 'weeks')
  );

  private readonly dayCount = computed(() => (this._showWeekends() ? 7 : 5));

  /** YYYY-MM-DD dates of the selected week in the timezone */
  readonly selectedDates = computed(() =>
    Array.from({ length: this.dayCount() }).map((_, i) =>
      this.weekStart().clone().add(i, 'days').format(DATE_INPUT_FORMAT)
    )
  );

  /**
   * As date strings, which the date range pipe shows as calendar dates rather
   * than shifting them into the browser's timezone.
   */
  readonly selectedDateRange = computed(() => ({
    start: this.selectedDates().at(0),
    end: this.selectedDates().at(-1),
  }));

  readonly isCurrentWeek = computed(() => this.weekOffset() === 0);

  navigateBack() {
    this.weekOffset.update((offset) => Math.max(0, offset - 1));
  }

  navigateForward() {
    this.weekOffset.update((offset) => offset + 1);
  }

  navigateToCurrentWeek() {
    this.weekOffset.set(0);
  }

  toggleWeekends(value: MatSlideToggleChange) {
    this._showWeekends.set(value.checked);
  }
}
